import { useState, type FC, type FormEvent } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'release', label: 'Release' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'case_study', label: 'Case Study' },
  { value: 'custom', label: 'Custom' },
] as const;

const PERSONAS = [
  { value: 'cto', label: 'CTO' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'ceo_startup', label: 'CEO Startup' },
  { value: 'qa_lead', label: 'QA Lead' },
  { value: 'general', label: 'Général' },
] as const;

export interface ContentFormData {
  eventType: string;
  topic: string;
  persona: string;
  context: string;
}

interface ContentInputFormProps {
  onSubmit: (data: ContentFormData) => void;
  isLoading: boolean;
}

const ContentInputForm: FC<ContentInputFormProps> = ({ onSubmit, isLoading }) => {
  const [eventType, setEventType] = useState('release');
  const [topic, setTopic] = useState('');
  const [persona, setPersona] = useState('general');
  const [context, setContext] = useState('');

  const isValid = topic.trim().length >= 10;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;
    onSubmit({ eventType, topic: topic.trim(), persona, context: context.trim() });
  };

  const selectClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

  const textareaClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none resize-none transition-colors';

  return (
    <div className="fc-card p-5">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
        Paramètres de génération
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Type d'événement */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              Type d'événement
            </label>
            <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={selectClasses}>
              {EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Persona cible */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              Persona cible
            </label>
            <select value={persona} onChange={(e) => setPersona(e.target.value)} className={selectClasses}>
              {PERSONAS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sujet / Description */}
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
            Sujet / Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Décrivez le sujet de votre contenu (min. 10 caractères)..."
            rows={3}
            className={textareaClasses}
          />
          {topic.length > 0 && topic.trim().length < 10 && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">Minimum 10 caractères requis</p>
          )}
        </div>

        {/* Contexte additionnel */}
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
            Contexte additionnel <span className="text-slate-400 dark:text-slate-500">(optionnel)</span>
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Informations supplémentaires, données clés, ton souhaité..."
            rows={2}
            className={textareaClasses}
          />
        </div>

        {/* Bouton */}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={!isValid || isLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {isLoading ? 'Génération en cours...' : 'Générer les 3 formats'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentInputForm;
